import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { FileText, Download, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' }
];

export default function ClientInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      toast.error('Error al cargar facturas');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (invoice) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoice.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', invoice.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Factura descargada');
    } catch (error) {
      toast.error('Error al descargar factura');
    }
  };

  const getMonthName = (month) => MONTHS.find(m => m.value === month)?.label || month;

  return (
    <Layout title="Mis Facturas">
      <div className="space-y-6 animate-fade-in" data-testid="client-invoices">
        <p className="text-slate-400">Consulta y descarga tus facturas mensuales</p>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Cargando...</div>
        ) : invoices.length === 0 ? (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No hay facturas disponibles</p>
              <p className="text-sm text-slate-500 mt-1">Las facturas aparecerán aquí cuando estén disponibles</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {invoices.map((invoice) => (
              <Card key={invoice.id} className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-amber-400" />
                      </div>
                      <div>
                        <h3 className="font-medium text-white">{invoice.file_name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="flex items-center gap-1 text-sm text-slate-400">
                            <Calendar className="w-4 h-4" />
                            {getMonthName(invoice.month)} {invoice.year}
                          </span>
                        </div>
                        {invoice.description && (
                          <p className="text-sm text-slate-500 mt-1">{invoice.description}</p>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => handleDownload(invoice)}
                      className="bg-sky-600 hover:bg-sky-500 text-white gap-2"
                      data-testid={`download-invoice-${invoice.id}`}
                    >
                      <Download className="w-4 h-4" />
                      Descargar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
